import { useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Button from 'components/Button'
import {
  CampaignCreateFormSchemas,
  CampaignCreateFormTypes,
} from 'schemas/campaign-voucher-schemas'
import { useCampaignCreate } from './use-campaign-create'

import { toast } from 'react-toastify'
import Dropdown from 'components/Dropdown'
import { DropDownItem } from 'types/components'

export const CreateNewCampaignForm = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {

  const formDefaultValues: CampaignCreateFormTypes = {
    campaignName: '',
    campaignCode: '',
    externalReference: '',
    startDate: '',
    endDate: '',
    description: '',
    statusId: 0,
    programUserId: 0,
    flCommit: null, //pass null as its handled in the backend
  }

  const form = useForm<CampaignCreateFormTypes>({
    resolver: zodResolver(CampaignCreateFormSchemas),
    defaultValues: formDefaultValues,
  })

  const {
    mutate: mutateCampaignCreate,
    isSuccess: isSuccessCampaignCreate,
    isError: isErrorCampaignCreate,
    isLoading: isLoadingCampaignCreate,
  } = useCampaignCreate()

  const onSubmit = useCallback(
    (data: CampaignCreateFormTypes) => {
      const payload = {
        ...data,
        startDate: data.startDate ? `${data.startDate} 00:00:00` : null,
        endDate: data.endDate ? `${data.endDate} 23:59:59` : null,
      }

      mutateCampaignCreate(
        { formData: payload },
        {
          onSuccess: () => {
            toast.success('Campaign created successfully!')
            onCloseModal()
          },
          onError: () => {
            // console.log('error')
          },
        })
    },
    [mutateCampaignCreate]
  )

  const STATUS_TYPES_OPTIONS = [
    { label: 'Inactive', value: 0 },
    { label: 'Active', value: 1 },
  ]

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          control={form.control}
          name="campaignName"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="campaignName"
              label="Campaign Name"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
            />
          )}
        />
        {form.formState.errors.campaignName && (
          <span className="text-red text-sm">
            {form.formState.errors.campaignName.message}
          </span>
        )}
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="campaignCode"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="campaignCode"
              label="Campaign Code"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
            />
          )}
        />
        {form.formState.errors.campaignCode && (
          <span className="text-red text-sm">
            {form.formState.errors.campaignCode.message}
          </span>
        )}
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="externalReference"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="externalReference"
              label="External Reference"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
            />
          )}
        />
        {form.formState.errors.externalReference && (
          <span className="text-red text-sm">
            {form.formState.errors.externalReference.message}
          </span>
        )}
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="description"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="description"
              label="Description"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="statusId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              title="Status"
              value={STATUS_TYPES_OPTIONS.find(
                (option) => option.value === value
              ) || null}
              options={STATUS_TYPES_OPTIONS.map((option) => ({
                ...option,
                value: String(option.value),
              }))}
              className="w-64 tracking-wider mb-5"
              borderColor="#e8ebef"
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
            />
          )}
        />
        {form.formState.errors.statusId && (
          <span className="text-red text-sm">
            {form.formState.errors.statusId.message}
          </span>
        )}
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="flex justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoadingCampaignCreate}
        >
          <span className="tracking-wider font-bold">
            {isLoadingCampaignCreate ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>
        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>
    </form >
  )
}
